import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useGlobalToast } from '../UI/toaster';
import { NotificationDropdown } from './NotificationDropdown';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { logError } from '../../utils/logger';
import {
  Settings,
  User,
  Info,
  Trash2,
  // AlertTriangle,
  PanelLeft,
  ChevronDown,
} from 'lucide-react';
import { IconButton } from '@/components/UI/Button';

interface HeaderProps {
  onToggleSidebar: () => void;
  isMobileViewport: boolean;
  isMobileSidebarOpen: boolean;
}

// Map a backend role identifier to a human-readable label for the
// user-menu dropdown. Snake-cased role names from `seed.py` (e.g.
// `finance_admin`) become space-separated title-cased strings
// ("Finance Admin").
function formatRoleLabel(role: string | null | undefined): string {
  if (!role) return 'User';
  return role
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

export const Header: React.FC<HeaderProps> = ({
  onToggleSidebar,
  isMobileViewport,
  isMobileSidebarOpen,
}) => {
  const { user, logout } = useAuth();
  const { success } = useGlobalToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  // SmartCHG title styling — sized to balance the larger CHG mark next
  // to it. The mark sets the vertical anchor (~48px on desktop), so the
  // wordmark steps up to 22-26px responsive. ``leading-none`` keeps the
  // text optically aligned with the logo's mid-line.
  const smartChgTextClass =
    'font-display text-[22px] sm:text-[24px] md:text-[26px] font-normal leading-none tracking-normal text-chg-burgundy-700 select-none';

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    };

    if (userDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userDropdownOpen]);

  const handleLogout = () => {
    try {
      logout(); // Logout is synchronous, no need to await
      success('Logged out successfully', 'You have been logged out of your account');
      navigate('/login');
    } catch (error) {
      logError('Header', 'Logout error', error instanceof Error ? error : new Error('Unknown error'));
    }
  };
  return (
    <header className="bg-card text-card-foreground flex items-center justify-between shadow-sm border-b border-border flex-shrink-0" style={{
      height: '3.5rem',
      paddingLeft: '1.5rem',
      paddingRight: '1rem',
      paddingTop: '0.625rem',
      paddingBottom: '0.625rem'
    }}>
      <div className="flex items-center" style={{ gap: '0.625rem' }}>
        {isMobileViewport && (
          <IconButton
            onClick={onToggleSidebar}
            icon={<PanelLeft className={`h-5 w-5 transition-transform duration-300 ${isMobileSidebarOpen ? 'rotate-180' : ''}`} />}
            aria-label={isMobileSidebarOpen ? 'Close menu' : 'Open menu'}
            variant="ghost"
            size="sm"
            className="border border-border bg-card text-muted-foreground shadow-sm hover:bg-muted hover:text-foreground touch-manipulation lg:hidden"
          />
        )}

        <Link
          to="/"
          className="flex items-center gap-3 min-w-0"
        >
          <img
            src="/brand/chg-logo.png"
            alt="CHG"
            className="h-10 sm:h-11 md:h-12 w-auto select-none shrink-0"
            draggable={false}
          />
          <span
            className={smartChgTextClass}
            aria-label="SmartCHG"
          >
            SmartCHG
          </span>
        </Link>

        {/* Search Bar - Compact Width */}
        {/* <form onSubmit={handleSearch} className="relative hidden md:block">
          <Search className="absolute text-neutral-500" style={{ left: 'var(--padding-sm)', top: '50%', transform: 'translateY(-50%)', width: 'var(--icon-sm)', height: 'var(--icon-sm)' }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search assets, rules, users..."
            className="border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-neutral-50 text-neutral-700 transition-all placeholder-neutral-500 futuristic-input search-input"
            style={{ paddingLeft: 'var(--space-2xl)', paddingRight: 'var(--padding-sm)', paddingTop: 'var(--padding-sm)', paddingBottom: 'var(--padding-sm)', width: '400px' }}
          />
        </form> */}
      </div>

      <div className="flex items-center" style={{ gap: '0.5rem' }}>
        {/* Notifications */}
        <NotificationDropdown
          isOpen={notificationDropdownOpen}
          onToggle={() => setNotificationDropdownOpen(!notificationDropdownOpen)}
        />

        {/* Help - Hidden on mobile */}
        <IconButton
          icon={<Info className="w-5 h-5" />}
          variant="ghost"
          size="sm"
          onClick={() => {
            window.open('https://chguenther.sharepoint.com/:b:/r/sites/SmartCHG/Shared%20Documents/General/User%20Documentation/Reference%20Guide%20-%20SmartCHG.pdf?csf=1&web=1&e=R3VWaY', '_blank', 'noopener,noreferrer');
          }}
          aria-label="Help"
          title="SmartCHG Guide"
          className="text-muted-foreground hover:text-foreground transition-colors hidden lg:flex"
        />

        {/* User Menu */}
        <div className="relative" ref={userDropdownRef}>
          <button
            onClick={() => setUserDropdownOpen(!userDropdownOpen)}
            className={`flex items-center rounded-xl border transition-all duration-200 touch-manipulation ${
              userDropdownOpen
                ? 'border-border bg-muted shadow-sm'
                : 'border-transparent bg-transparent hover:border-border hover:bg-muted hover:shadow-sm'
            }`}
            style={{ gap: '0.5rem', padding: '0.375rem 0.5rem' }}
          >
            <div
              className="bg-primary rounded-full flex items-center justify-center shadow-sm"
              style={{ width: '1.75rem', height: '1.75rem' }}
            >
              <span className="text-primary-foreground font-medium" style={{ fontSize: '0.95rem', lineHeight: '1' }}>
                {user?.email?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="hidden sm:block text-left leading-tight">
              <p className="font-medium text-card-foreground" style={{ fontSize: '0.9rem', lineHeight: '1.1' }}>
                {user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.email || 'User'
                }
              </p>
              <p className="text-muted-foreground" style={{ fontSize: '0.8rem', lineHeight: '1.1', marginTop: '0.15rem' }}>
                {formatRoleLabel(user?.role)}
              </p>
            </div>
            <ChevronDown
              className={`text-muted-foreground transition-transform hidden sm:block ${userDropdownOpen ? 'rotate-180 text-foreground' : ''}`}
              style={{ width: '0.85rem', height: '0.85rem' }}
            />
          </button>

          {/* User Dropdown */}
          {userDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 sm:w-56 bg-popover text-popover-foreground rounded-xl shadow-lg border border-border py-2 z-50">
              <div className="px-4 py-3 border-b border-border">
                <p className="text-label text-popover-foreground">
                  {user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user?.email || 'User'
                  }
                </p>
                <p className="text-caption text-muted-foreground">
                  {formatRoleLabel(user?.role)}
                </p>
              </div>

              <div className="py-1">
                <button
                  onClick={() => {
                    setUserDropdownOpen(false);
                    navigate('/settings?tab=profile');
                  }}
                  className="flex items-center gap-3 w-full px-4 py-2 text-body-small text-popover-foreground hover:bg-muted transition-colors"
                >
                  <User className="w-4 h-4" />
                  Profile
                </button>

                {/* <button
                  onClick={() => {
                    setUserDropdownOpen(false);
                    navigate('/settings');
                  }}
                  className="flex items-center gap-3 w-full px-4 py-2 text-body-small text-popover-foreground hover:bg-muted transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  Settings
                </button> */}

                {/* <button
                    onClick={() => {
                      setUserDropdownOpen(false);
                      navigate('/settings?tab=security');
                    }}
                    className="flex items-center gap-3 w-full px-4 py-2 text-body-small text-neutral-700 hover:bg-neutral-50 transition-colors"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    Security
                  </button> */}
              </div>

              <div className="border-t border-border pt-1">
                <button
                  onClick={() => {
                    setUserDropdownOpen(false);
                    handleLogout();
                  }}
                  className="flex items-center gap-3 w-full px-4 py-2 text-body-small text-error-600 hover:bg-error-50 transition-colors font-medium"
                >
                  <Trash2 className="w-4 h-4" />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Search (visible only on mobile) */}
      {/* <div className="md:hidden absolute top-16 left-0 right-0 bg-white border-b border-neutral-200 p-4 z-30">
        <form onSubmit={handleSearch} className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 w-4 h-4" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="w-full pl-10 pr-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-neutral-50 text-neutral-700 transition-all placeholder-neutral-500 futuristic-input search-input touch-manipulation"
          />
        </form>
      </div> */}
    </header>
  );
};
